import { useEffect } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { Box, IconButton, Divider, Tooltip } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';

type Props = {
  html: string;
  onChange: (html: string) => void;
  minHeight?: number;
};

const ToolbarButton = ({
  active,
  disabled,
  title,
  onClick,
  children,
}: {
  active?: boolean;
  disabled?: boolean;
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <Tooltip title={title}>
    <span>
      <IconButton
        size="small"
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
        disabled={disabled}
        sx={{ color: active ? 'primary.main' : 'text.secondary', borderRadius: 1 }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const Toolbar = ({ editor }: { editor: Editor }) => {
  const promptLink = () => {
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', previous || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        px: 0.5,
        py: 0.25,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.default',
      }}
    >
      <ToolbarButton title="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
        <FormatBoldIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton title="Italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
        <FormatItalicIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton title="Underline" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}>
        <FormatUnderlinedIcon fontSize="small" />
      </ToolbarButton>
      <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />
      <ToolbarButton title="Bullet list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
        <FormatListBulletedIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton title="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
        <FormatListNumberedIcon fontSize="small" />
      </ToolbarButton>
      <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />
      <ToolbarButton title="Add/edit link" active={editor.isActive('link')} onClick={promptLink}>
        <LinkIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Remove link"
        disabled={!editor.isActive('link')}
        onClick={() => editor.chain().focus().unsetLink().run()}
      >
        <LinkOffIcon fontSize="small" />
      </ToolbarButton>
      <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />
      <ToolbarButton title="Undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}>
        <UndoIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton title="Redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}>
        <RedoIcon fontSize="small" />
      </ToolbarButton>
    </Box>
  );
};

export const EmailHtmlEditor = ({ html, onChange, minHeight = 240 }: Props) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
    ],
    content: html || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Keep the editor in sync if the incoming html changes externally (e.g. after
  // the preview fetch resolves). Only reset when the value really differs to
  // avoid clobbering the user's in-progress edits on every keystroke.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (html && html !== current) {
      editor.commands.setContent(html, false);
    }
  }, [html, editor]);

  if (!editor) return null;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        '& .ProseMirror': {
          minHeight,
          maxHeight: 420,
          overflowY: 'auto',
          p: 1.5,
          outline: 'none',
          fontSize: 14,
          lineHeight: 1.5,
          // The templates now space paragraphs with a small bottom margin (no
          // blank-line spacers), so mirror that here — a clean single gap
          // between paragraphs, not a full empty line.
          '& p': { margin: '0 0 0.7em' },
          '& h1, & h2, & h3, & h4': { margin: '0.7em 0 0.2em', fontWeight: 600, lineHeight: 1.3 },
          // Restore list bullets/numbers (MUI's CssBaseline strips list-style).
          '& ul': { listStyle: 'disc', paddingLeft: '1.5em', margin: '0 0 0.7em' },
          '& ol': { listStyle: 'decimal', paddingLeft: '1.5em', margin: '0 0 0.7em' },
          '& li': { margin: '0.15em 0' },
          '& li > p': { margin: 0 },
          '& a': { color: 'primary.main', textDecoration: 'underline' },
          '& img': { maxWidth: '100%', height: 'auto' },
        },
      }}
    >
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </Box>
  );
};
